import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Mock PDF jobs data
const mockJobs = [
  {
    id: 1,
    name: 'Invoice Batch #001',
    template: 'Invoice Template',
    status: 'completed',
    created_at: '2025-06-20T10:30:00Z',
    pdf_count: 25,
    download_url: '#'
  },
  {
    id: 2,
    name: 'Employee Welcome Letters',
    template: 'Employee Letter Template',
    status: 'processing',
    created_at: '2025-06-20T11:15:00Z',
    pdf_count: 12,
    download_url: null
  },
  {
    id: 3,
    name: 'Monthly Statement Batch',
    template: 'Invoice Template',
    status: 'completed',
    created_at: '2025-06-19T14:20:00Z',
    pdf_count: 48,
    download_url: '#'
  },
  {
    id: 4,
    name: 'Contract Package',
    template: 'Contract Agreement',
    status: 'failed',
    created_at: '2025-06-19T09:45:00Z',
    pdf_count: 0,
    download_url: null
  }
];

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, type: '', message: '' });
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));
      setJobs(mockJobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      processing: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Enhanced download handler for mock interactivity
  const handleDownload = (job) => {
    if (job.status === 'completed' && job.download_url) {
      setToast({ show: true, type: 'success', message: `Download started for ${job.name}` });
      setTimeout(() => setToast({ show: false, type: '', message: '' }), 3000);
    } else {
      setToast({ show: true, type: 'error', message: `Download not available for ${job.name}` });
      setTimeout(() => setToast({ show: false, type: '', message: '' }), 3000);
    }
  };

  // Enhanced retry handler for mock interactivity
  const handleRetry = (job) => {
    setJobs((prev) => prev.map(j => j.id === job.id ? { ...j, status: 'processing' } : j));
    setToast({ show: true, type: 'info', message: `Retrying job for ${job.name}...` });
    setTimeout(() => {
      setJobs((prev) => prev.map(j => j.id === job.id ? { ...j, status: 'completed', download_url: '#' } : j));
      setToast({ show: true, type: 'success', message: `Job for ${job.name} completed!` });
      setTimeout(() => setToast({ show: false, type: '', message: '' }), 3000);
    }, 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-stone-50 to-amber-50">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-lg shadow-lg text-white font-semibold transition-all duration-300 ${toast.type === 'success' ? 'bg-green-600' : toast.type === 'error' ? 'bg-red-600' : 'bg-amber-500'}`}>
          {toast.message}
        </div>
      )}
      {/* Navigation */}
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/dashboard" className="text-xl font-semibold text-gray-900 hover:text-blue-600">
                Letterly
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium"
              >
                Dashboard
              </Link>
              <Link
                to="/templates"
                className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium"
              >
                Templates
              </Link>
              <span className="text-gray-700">Welcome, {user?.email}</span>
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">PDF Generation Jobs</h1>
          <Link
            to="/upload"
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Create New Job
          </Link>
        </div>

        {jobs.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No PDF jobs</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first PDF generation job.
            </p>
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {jobs.map((job) => (
                <li key={job.id}>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 rounded-lg bg-blue-500 flex items-center justify-center">
                            <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-blue-600 truncate">
                              {job.name}
                            </p>
                            {getStatusBadge(job.status)}
                          </div>
                          <div className="mt-1 flex items-center text-sm text-gray-500">
                            <p>Template: {job.template}</p>
                            <p className="ml-4">PDFs: {job.pdf_count}</p>
                            <p className="ml-4">Created: {formatDate(job.created_at)}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {job.status === 'processing' && (
                          <div className="flex items-center space-x-1">
                            <span className="animate-spin h-4 w-4 border-2 border-amber-600 border-t-transparent rounded-full"></span>
                            <span className="text-xs text-amber-600 font-medium">Processing...</span>
                          </div>
                        )}
                        {job.status === 'completed' && job.download_url && (
                          <button
                            onClick={() => handleDownload(job)}
                            className="inline-flex items-center px-3 py-1 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                          >
                            Download ZIP
                          </button>
                        )}
                        {job.status === 'failed' && (
                          <button
                            onClick={() => handleRetry(job)}
                            className="inline-flex items-center px-3 py-1 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                          >
                            Retry
                          </button>
                        )}
                        <button
                          className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-8">
          <Link
            to="/dashboard"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Jobs;
